import type { ActionBehaviours, ActionHandler } from '../tools/do-action.tool';
import { ConfirmWaits, OverlayToCommitDeps, overlayToCommit, Prefilled, Prepared, pressToCommit, PressToCommitDeps } from './confirm-lane';

/** A screen's part in a confirm action: fill its own form for the builder's press, or word a card for the builder's click. */
export type ConfirmStep =
  | { press: (values: Record<string, unknown>) => Promise<Prefilled> }
  | { overlay: (values: Record<string, unknown>) => Promise<Prepared> };

interface Offer {
  step: ConfirmStep;
  gone: boolean;
  whenGone: Set<() => void>;
}

const offered = new Map<string, Offer>();

/**
 * A mounted screen offers the confirm actions it can take, and withdraws them when it goes. Going ends any wait on it,
 * because a card left up would run its action against a screen that is no longer there. Free of Angular so the desktop
 * app's scripted checks load this very file.
 */
export function offerConfirmSteps(steps: Record<string, ConfirmStep>): () => void {
  const mine = Object.entries(steps).map(([id, step]) => {
    const offer: Offer = { step, gone: false, whenGone: new Set() };
    offered.set(id, offer);
    return [id, offer] as const;
  });
  return () => {
    for (const [id, offer] of mine) {
      offer.gone = true;
      for (const end of offer.whenGone) end();
      // The next screen is built before the last one is destroyed, and may have offered the same id already.
      if (offered.get(id) === offer) offered.delete(id);
    }
  };
}

const LEFT = 'the builder left the screen';

export interface OnScreenConfirmDeps {
  waits: Pick<ConfirmWaits, 'begin' | 'saving' | 'report' | 'close'>;
  highlight: PressToCommitDeps['highlight'];
  overlay: OverlayToCommitDeps['overlay'];
}

/** Runs whichever step the screen on show offers for the row. Its commits report under the row's id. */
export function confirmOnScreen(deps: OnScreenConfirmDeps): ActionHandler {
  return async (values, ctx, row) => {
    const offer = offered.get(row.id);
    if (!offer) {
      const where = row.panel ? `the ${row.panel} panel of ${row.screen}` : row.screen;
      return { outcome: 'refused', refusal: `${row.label} is on ${where}, which isn't on the builder's screen. Nothing was changed; call navigate first.` };
    }
    // Only a wait already running hears the report, so a screen gone during its own prefill is caught after it.
    const leftDuring = { refused: `The builder left the screen while ${row.label} was being prepared. Nothing was changed.` };
    // Was (2026-10-30): const end = () => deps.waits.report(row.id, { outcome: 'closed', because: LEFT });
    // A report isn't heard during a prefill, and one that hangs on a screen that has gone would hold the slot for the whole limit.
    const end = () => deps.waits.close(row.id, LEFT);
    offer.whenGone.add(end);
    try {
      const { step } = offer;
      if ('press' in step) {
        return await pressToCommit({
          waits: deps.waits, highlight: deps.highlight, commit: row.id,
          prefill: async (v) => { const filled = await step.press(v); return offer.gone ? leftDuring : filled; },
        })(values, ctx, row);
      }
      return await overlayToCommit({
        waits: deps.waits, overlay: deps.overlay,
        prepare: async (v) => { const prepared = await step.overlay(v); return offer.gone ? leftDuring : prepared; },
      })(values, ctx, row);
    } finally {
      offer.whenGone.delete(end);
    }
  };
}

const ON_SCREEN = [
  'team-add-member', 'change-team-level', 'change-app-role', 'team-remove-member',
  'delete-page', 'create-project',
  'set-workspace-invites', 'set-workspace-adds', 'set-project-invites', 'set-project-signups',
  'resend-invite', 'cancel-invite', 'set-mobile-access-mode',
  'publish', 'revert-version', 'unpublish', 'rebuild', 'set-works-offline', 'set-time-zone', 'build-live-apk', 'build-iphone',
  'iphone-public-link', 'iphone-nudge', 'connect-apple', 'approve-privacy-policy', 'submit-to-app-store', 'remove-screenshot', 'set-runtime-test-password', 'rotate-test-login',
];

export function makeOtherConfirmBehaviours(deps: OnScreenConfirmDeps): ActionBehaviours {
  const confirm = confirmOnScreen(deps);
  return Object.fromEntries(ON_SCREEN.map((id) => [id, { confirm }]));
}
